'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { AuthAppShell } from 'src/components/DS';
import { useAuth } from 'src/context/AuthContext';
import { COORDINATOR_ROUTES, VENDOR_ROUTES } from 'src/shared/constants/routes';
import { checkVendorDomain } from 'src/shared/utils/hosts';
import { safeInternalRedirect } from 'src/shared/utils/redirects';
import AuthCopyright from './AuthCopyright';

const AuthLayout = ({ children }: { children?: React.ReactNode }) => {
  const { checkAuth, isAuthInitialized, isRequesting } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isUserExist, setIsUserExist] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    // Wait until the AWS/Amplify config fetch has settled before checking the
    // session — checkAuth() reads tokens from storage that only exists after
    // configureAmplify() runs (which flips isAuthInitialized to true).
    if (isRequesting) {
      return;
    }
    // Config fetch finished but Amplify was never configured (e.g. invalid
    // domain or a server error) — fall back to rendering the login view.
    if (!isAuthInitialized) {
      setIsUserExist(false);
      return;
    }
    const checkAuthenticatedUser = async () => {
      try {
        const { isAuthenticated } = await checkAuth();
        setIsUserExist(isAuthenticated);
      } catch (_error) {
        setIsUserExist(false);
      }
    };
    checkAuthenticatedUser();
  }, [checkAuth, isAuthInitialized, isRequesting]);

  useEffect(() => {
    if (isUserExist === true) {
      const isVendor = checkVendorDomain();
      const rawRedirect = sessionStorage.getItem('redirectUrl') || searchParams.get('redirectUrl');
      const redirectUrl = safeInternalRedirect(rawRedirect);
      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        globalThis.location.href = redirectUrl;
      } else {
        // Evict a rejected candidate so it cannot shadow later redirects — see Login.tsx. [APX-387]
        sessionStorage.removeItem('redirectUrl');
        router.replace(isVendor ? VENDOR_ROUTES.HOME : COORDINATOR_ROUTES.HOME);
      }
    }
  }, [isUserExist, router, searchParams]);

  // To avoid flicker effect - show nothing during initial auth check
  if (typeof isUserExist === 'undefined') {
    return null;
  }

  if (isUserExist) {
    return null; // Will redirect via useEffect above
  }

  // The signed-out frame: canvas, the centred card, the copyright line. `data-lula-ds` scopes
  // the DS reset and the Bootstrap compatibility rules to this subtree.
  return (
    <AuthAppShell data-lula-ds="" footer={<AuthCopyright />}>
      {children}
    </AuthAppShell>
  );
};

export default AuthLayout;
